import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import { useState, useMemo, useContext, useEffect, useCallback } from 'react';
import { isMobile } from 'react-device-detect';
import { MapContainer, TileLayer, SVGOverlay } from 'react-leaflet';
import { ReactSVG } from 'react-svg';

import { ReactComponent as Logo } from 'assets/icons/logo_aplus.svg';
import 'data/edifices/A/tiers/tier.css';
// Here Add more .css files from data/edifices/*/tiers/tier.css
import CompassRose from 'assets/icons/north.svg';
import ApartamentsTable from 'components/ApartamentsTable';
import FloorSwitcher from 'components/FloorSwitcher';
import Legend from 'components/Legend';
import Tooltip from 'components/Tooltip';
import { config } from 'config';
import { ApartmentsFilterContext } from 'context/apartmentsFilterContext';
import { DataContext } from 'context/dataContext';
import { NavigationContext } from 'context/navigationContext';
import { TranslationContext } from 'context/translationContext';
import aEdifice from 'data/edifices/A/edifice.json';
// Uncomment when two buildings
// import bEdifice from 'data/edifices/B/edifice.json';
import projectData from 'data/project.json';
import PZT from 'data/pzt.svg';
import { importAllFiles } from 'utils/importAllFiles';

import styles from './styles.module.scss';

const PZT_VIEW_BOX_X = 1600;
const PZT_VIEW_BOX_Y = 1200;

const FloorsView = () => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [ofertNo, setOfertNo] = useState('');
  const [roomsAmount, setRoomsAmount] = useState('');
  const [area, setArea] = useState('');
  const [currentFloor, setCurrentFloor] = useState(0);
  const [isMoved, setIsMoved] = useState(false);
  const {
    selectedFloor,
    setSelectedFloor,
    setActiveNavigationItem,
    setSelectedAptId,
  } = useContext(NavigationContext);
  const { t } = useContext(TranslationContext);
  const { filterValues } = useContext(ApartmentsFilterContext);
  const { apartments, maxRooms } = useContext(DataContext);
  const { roomMin, roomMax } = filterValues;

  useEffect(() => {
    if (selectedFloor) {
      setCurrentFloor(selectedFloor);
      setSelectedFloor(null);
    }
  }, [selectedFloor]);

  const isMapHidden = useMemo(() => config.isMapHidden, []);

  const position: LatLngExpression = useMemo(() => {
    const { address } = projectData;
    return [address.geo_cenlat, address.geo_cenlon];
  }, []);

  const bounds: LatLngBoundsExpression = useMemo(() => {
    const { address } = projectData;
    return [
      [address.geo_r1lat, address.geo_r1lon],
      [address.geo_r2lat, address.geo_r2lon],
    ];
  }, []);

  const floors = useMemo(() => {
    const parsedFloors = [];

    const aBuilidingFloors = importAllFiles(
      require.context('data/edifices/A/tiers', false, /\.(svg)$/),
    );

    // Uncomment if two buildings
    // const bBuilidingFloors = importAllFiles(
    //   require.context('data/edifices/B/tiers', false, /\.(svg)$/),
    // );

    // @ts-ignore
    const uniqueAFloors = [...new Set(aBuilidingFloors)];
    // Uncomment if two buildings
    // @ts-ignore
    // const uniqueBFloors = [...new Set(bBuilidingFloors)];

    let floorsLength = uniqueAFloors.length;
    // Uncomment if two buildings
    // if (uniqueBFloors.length > floorsLength) {
    //   floorsLength = uniqueBFloors.length;
    // }

    for (let i = 0; i < floorsLength; i++) {
      const floorSVGs = [];
      if (!!uniqueAFloors[i]) {
        floorSVGs.push({
          url: uniqueAFloors[i],
          scale: aEdifice.pzt_w,
          rotate: aEdifice.pzt_ang,
          translateX: parseFloat((PZT_VIEW_BOX_X * aEdifice.pzt_x).toFixed(4)),
          translateY: parseFloat((PZT_VIEW_BOX_Y * aEdifice.pzt_y).toFixed(4)),
        });
      }

      // Uncomment if two buildings
      // if (!!uniqueBFloors[i]) {
      //   floorSVGs.push({
      //     url: uniqueBFloors[i],
      //     scale: bEdifice.pzt_w,
      //     rotate: bEdifice.pzt_ang,
      //     translateX: parseFloat((PZT_VIEW_BOX_X * bEdifice.pzt_x).toFixed(4)),
      //     translateY: parseFloat((PZT_VIEW_BOX_Y * bEdifice.pzt_y).toFixed(4)),
      //   });
      // }
      parsedFloors.push(floorSVGs);
    }

    return parsedFloors;
  }, []);

  const handleHigherFloorClick = () => {
    if (currentFloor + 1 < floors.length) {
      setCurrentFloor(prevState => prevState + 1);
    }
  };

  const handleLowerFloorClick = () => {
    if (currentFloor !== 0) {
      setCurrentFloor(prevState => prevState - 1);
    }
  };

  const moveElements = () => {
    if (!isMoved) {
      const pztFloor = document.getElementsByClassName('bu');
      const DOMFloors = document.getElementsByClassName('floor');
      if (pztFloor.length !== 0 && DOMFloors.length !== 0) {
        for (let i = 0; i < DOMFloors.length; i++) {
          const injectedSvg = DOMFloors[i].querySelector('.injected-svg');
          if (injectedSvg) {
            DOMFloors[i].innerHTML = injectedSvg.innerHTML;
          }
          pztFloor[0].after(DOMFloors[i]);
        }
      }
      setIsMoved(true);
    }
  };

  const fillTooltipValues = useCallback((e: any) => {
    const apartNo = e.target.getAttribute('data-ap');
    const id = e.target.getAttribute('toid');

    if (apartNo && id) {
      const aptData = apartments.find(apt => apt.id === id);

      if (aptData) {
        setOfertNo(apartNo);
        setArea(aptData.area.toString());
        setRoomsAmount(aptData.rooms.toString());
      }
    }
  }, []);

  const openTooltip = useCallback((e: any) => {
    setIsTooltipOpen(true);
  }, []);

  const hideTooltip = useCallback(() => {
    setIsTooltipOpen(false);
  }, []);

  const handleClick = useCallback((e: any) => {
    const id = e.target.getAttribute('toid');
    if (id) {
      setSelectedAptId(id);
      setActiveNavigationItem(3);
    }
  }, []);

  const addApartmentRoomsClasses = () => {
    const visibleFloor = document.getElementsByClassName('floor-visible');
    if (visibleFloor.length !== 0) {
      const flatsPath = visibleFloor[0].querySelectorAll('.rm');
      flatsPath.forEach(path => {
        const id = path.getAttribute('toid');
        const apartmentInfo = apartments.find(apt => apt.id === id);
        if (apartmentInfo) {
          const aptRooms = apartmentInfo.rooms;
          const parsedRoomMin = parseInt(roomMin);
          const parsedRoomMax = parseInt(roomMax);

          if (parsedRoomMax && parsedRoomMax) {
            if (aptRooms >= parsedRoomMin && aptRooms <= parsedRoomMax) {
              path.classList.add(`room-${aptRooms}`);
            } else {
              path.classList.remove(`room-${aptRooms}`);
            }
          } else {
            path.classList.add(`room-${aptRooms}`);
          }
        }
      });
    }
  };

  useEffect(() => {
    addApartmentRoomsClasses();
  }, [roomMin, roomMax]);

  useEffect(() => {
    return () => {
      const DOMApartaments = document.getElementsByClassName('rm');
      const DOMFloors = document.getElementsByClassName('floor');

      for (let i = 0; i < DOMApartaments.length; i++) {
        if (!isMobile) {
          DOMApartaments[i].removeEventListener(
            'mouseenter',
            fillTooltipValues,
          );
        }
        DOMApartaments[i].removeEventListener('click', handleClick);
      }

      if (!isMobile) {
        for (let i = 0; i < DOMFloors.length; i++) {
          DOMFloors[i].removeEventListener('mouseenter', openTooltip);
          DOMFloors[i].removeEventListener('mouseleave', hideTooltip);
        }
      }
    };
  }, []);

  const addListeners = () => {
    const DOMApartaments = document.getElementsByClassName('rm');
    const DOMFloors = document.getElementsByClassName('floor');

    for (let i = 0; i < DOMApartaments.length; i++) {
      if (!isMobile) {
        DOMApartaments[i].addEventListener('mouseenter', fillTooltipValues);
      }
      DOMApartaments[i].addEventListener('click', handleClick);
    }

    if (!isMobile) {
      for (let i = 0; i < DOMFloors.length; i++) {
        DOMFloors[i].addEventListener('mouseenter', openTooltip);
        DOMFloors[i].addEventListener('mouseleave', hideTooltip);
      }
    }
  };

  const handleAfterInjection = (floorIdx: number, buildingIdx: number) => {
    if (
      floorIdx + 1 === floors.length &&
      buildingIdx + 1 === floors[floorIdx].length
    ) {
      moveElements();
      addListeners();
      addApartmentRoomsClasses();
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.leftCol}>
          <div className={styles.wrapper}>
            <MapContainer center={position} zoom={18}>
              <div className={styles.tileWrapper}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxNativeZoom={21}
                  maxZoom={21}
                  className={isMapHidden ? 'mapHidden' : ''}
                />
              </div>
              <SVGOverlay
                bounds={bounds}
                interactive
                className={styles.overlay}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  x="0"
                  y="0"
                  viewBox="0 0 1600 1200"
                  preserveAspectRatio="xMidYMid slice"
                >
                  <g
                    style={{
                      transformOrigin: `center center`,
                      transform: `rotate(${projectData.address.north_ang}deg) scale(0.832322)`,
                    }}
                  >
                    <ReactSVG src={PZT} wrapper="svg" id="PZT" />
                    {floors.map((floor, outerIdx) => {
                      return floor.map((building, idx) => (
                        <g
                          key={building.url}
                          style={{
                            transform: `translate(${building.translateX}px, ${building.translateY}px) scale(${building.scale}) rotate(${building.rotate}deg)`,
                          }}
                          className={[
                            currentFloor !== outerIdx
                              ? styles.hidden
                              : 'floor-visible',
                            'floor',
                            'apartmentsWithOverlay',
                          ].join(' ')}
                        >
                          <ReactSVG
                            src={building.url}
                            className={styles.svgImage}
                            wrapper="svg"
                            afterInjection={() =>
                              handleAfterInjection(outerIdx, idx)
                            }
                          />
                        </g>
                      ));
                    })}
                  </g>
                </svg>
              </SVGOverlay>
            </MapContainer>
            <FloorSwitcher
              onLowerFloorClick={handleLowerFloorClick}
              onHigherFloorClick={handleHigherFloorClick}
              currentFloor={currentFloor}
            />
            <div className={styles.logoWrapper}>
              <a href="https://cadaplus.com/BIM+/" target="_blank">
                <Logo />
              </a>
            </div>
            <ReactSVG
              src={CompassRose}
              wrapper="svg"
              id="compass"
              className={styles.compassRose}
            />
          </div>
          <Legend maxRooms={maxRooms} />
        </div>
        <div className={styles.rightCol}>
          <ApartamentsTable data={apartments} />
        </div>
      </div>
      {!isMobile && (
        <Tooltip isOpen={isTooltipOpen}>
          <div className={styles.tooltip}>
            <p>
              {t('offerNumber')}: <b>{ofertNo}</b>
            </p>
            <p>
              {t('roomsAmount')}: <b>{roomsAmount}</b>
            </p>
            <p>
              {t('area')}:{' '}
              <b>
                {area}m<sup>2</sup>
              </b>
            </p>
          </div>
        </Tooltip>
      )}
    </>
  );
};

export default FloorsView;
